import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { api, errMsg, downloadFile } from "@/lib/api";
import { fmtDateTime, OUTCOME_CLS } from "@/lib/format";
import { PageHeader, NativeSelect, Badge, Loading, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Table = ({ head, children, testid }) => (
  <div className="card-surface overflow-x-auto">
    <table className="w-full min-w-[820px] text-sm" data-testid={testid}>
      <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr>{head.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export default function Participations() {
  const [params] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(params.get("event") || "");
  const [outcome, setOutcome] = useState("");
  const [parts, setParts] = useState(null);
  const [verifs, setVerifs] = useState(null);
  const [tab, setTab] = useState("parts");

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)); }, []);
  useEffect(() => {
    setParts(null); setVerifs(null);
    api.get("/participations", { params: { event_id: eventId } }).then((r) => setParts(r.data)).catch((e) => { toast.error(errMsg(e)); setParts([]); });
    api.get("/verifications", { params: { event_id: eventId, outcome } }).then((r) => setVerifs(r.data)).catch(() => setVerifs([]));
  }, [eventId, outcome]);

  const exp = (fmt) => {
    const dataset = tab === "parts" ? "participations" : "verifications";
    downloadFile(`/reports/export?dataset=${dataset}&fmt=${fmt}&event_id=${eventId}`, `msp-${dataset}.${fmt}`).catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div>
      <PageHeader eyebrow="Registro" title="Partecipazioni e verifiche" subtitle="Ogni scansione e ogni registrazione sono salvate nel database con esito, motivazione e operatore.">
        <Button variant="outline" onClick={() => exp("csv")} data-testid="participations-export-csv-btn"><FileDown className="mr-2 h-4 w-4" />CSV</Button>
        <Button variant="outline" onClick={() => exp("xlsx")} data-testid="participations-export-xlsx-btn"><FileDown className="mr-2 h-4 w-4" />Excel</Button>
      </PageHeader>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <NativeSelect value={eventId} onChange={setEventId} className="sm:min-w-[320px]" testid="participations-event-filter">
          <option value="">Tutte le manifestazioni</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </NativeSelect>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="parts" data-testid="tab-participations">Partecipazioni registrate {parts && `(${parts.length})`}</TabsTrigger>
          <TabsTrigger value="verifs" data-testid="tab-verifications">Registro verifiche (ammessi / non ammessi)</TabsTrigger>
        </TabsList>
        <TabsContent value="parts" className="mt-4">
          {parts === null ? <Loading /> : parts.length === 0 ? <Empty>Nessuna partecipazione registrata.</Empty> : (
            <Table testid="participations-table" head={["Data e ora", "Atleta", "Licenza", "Categorie", "Manifestazione", "Organizzatore", "Esito"]}>
              {parts.map((p) => (
                <tr key={p.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDateTime(p.registered_at)}</td>
                  <td className="px-4 py-3 font-semibold">{p.athlete_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.license_number}</td>
                  <td className="px-4 py-3 text-slate-600">{p.categories.join(", ")}</td>
                  <td className="px-4 py-3">{p.event_name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.organizer_name}</td>
                  <td className="px-4 py-3"><Badge cls={OUTCOME_CLS[p.outcome]}>{p.outcome}</Badge></td>
                </tr>
              ))}
            </Table>
          )}
        </TabsContent>
        <TabsContent value="verifs" className="mt-4">
          <NativeSelect value={outcome} onChange={setOutcome} className="mb-3" testid="verifications-outcome-filter">
            <option value="">Tutti gli esiti</option>
            {Object.keys(OUTCOME_CLS).map((o) => <option key={o}>{o}</option>)}
          </NativeSelect>
          {verifs === null ? <Loading /> : verifs.length === 0 ? <Empty>Nessuna verifica effettuata.</Empty> : (
            <Table testid="verifications-table" head={["Data e ora", "Atleta", "Licenza", "Operazione", "Esito", "Motivazione", "Organizzatore"]}>
              {verifs.map((v) => (
                <tr key={v.id} className="border-b border-slate-100">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDateTime(v.at)}</td>
                  <td className="px-4 py-3 font-semibold">{v.athlete_name || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{v.license_number || "—"}</td>
                  <td className="px-4 py-3 capitalize">{v.action}</td>
                  <td className="px-4 py-3"><Badge cls={OUTCOME_CLS[v.outcome]}>{v.outcome}</Badge></td>
                  <td className="px-4 py-3 text-red-700">{v.reasons.join("; ")}</td>
                  <td className="px-4 py-3 text-slate-600">{v.organizer_name}</td>
                </tr>
              ))}
            </Table>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
